/**
 * Vỏ ứng dụng PWA nhân viên.
 *
 * Bốn tab, lấy theo cấu trúc app nhân viên của Tanca nhưng CẮT BỚT cho đúng
 * phạm vi đề án: Tanca là bộ HRM đầy đủ (tuyển dụng, KPI, phiếu lương, giao
 * việc…), hệ thống này chỉ làm chấm công. Thêm tab không có backend chỉ tạo
 * ngõ cụt cho người dùng.
 *
 * Tên tab thứ hai đổi từ "Lịch sử" sang "Chọn tháng" theo bản thiết kế
 * `fe/bang-cham-cong-*.pdf` — nó nay chứa ba màn con: Công tháng, Công tuần,
 * Lịch & sự kiện.
 */

import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'

import {
  dangKyXuLyBatBuocDoiMatKhau, dangKyXuLyMatPhien, lamMoiToken, layToken,
} from './api/client'
import { startAutoSync } from './lib/queue'
import { chuCaiDau } from './lib/ten'
import { nhanVienHienTai } from './services/auth.service'
import { layTrangThaiFace } from './services/catalog.service'
import { Icon, KhoThongBao, RanhGioiLoi, type TenIcon } from './ui'

import CaCongTrinh from './pages/CaCongTrinh'
import CaNhan from './pages/CaNhan'
import ChamCong from './pages/ChamCong'
import DangNhap from './pages/DangNhap'
import DoiMatKhau from './pages/DoiMatKhau'
import LichSu from './pages/LichSu'
import ThongTinCaNhan from './pages/ThongTinCaNhan'
import YeuCau from './pages/YeuCau'

interface Tab {
  den: string
  nhan: string
  icon: TenIcon
}

const TABS: Tab[] = [
  { den: '/cham-cong', nhan: 'Chấm công', icon: 'dong-ho' },
  { den: '/lich-su', nhan: 'Chọn tháng', icon: 'lich' },
  { den: '/yeu-cau', nhan: 'Yêu cầu', icon: 'soan' },
  { den: '/ca-nhan', nhan: 'Cá nhân', icon: 'nguoi-dung' },
]

export default function App() {
  const [daDangNhap, setDaDangNhap] = useState(() => Boolean(layToken()))

  // Phải đổi mật khẩu trước khi làm gì khác (docs/14 §14.3).
  //
  // Hai nguồn bật cờ này, cần cả hai. Lúc ĐĂNG NHẬP: server trả
  // `must_change_password`, chuyển màn ngay thay vì để người dùng bấm vào một
  // chức năng rồi mới nhận 403. GIỮA PHIÊN: admin đặt lại mật khẩu trong lúc
  // nhân viên đang mở app, lần gọi API kế tiếp trả 403 — bắt ở tầng chung của
  // `request()`, nếu không thì mọi màn hình phải tự xử lý mã lỗi này.
  const [phaiDoiMatKhau, setPhaiDoiMatKhau] = useState(
    () => Boolean(nhanVienHienTai()?.must_change_password),
  )

  useEffect(
    () => dangKyXuLyBatBuocDoiMatKhau(() => setPhaiDoiMatKhau(true)),
    [],
  )

  // Refresh token hết hạn hoặc bị thu hồi. `client.ts` đã dọn localStorage.
  // ⚠️ KHÔNG đụng vào hàng đợi offline — trong đó có thể đang nằm giờ công
  // thật, và nó gửi được ngay khi người dùng đăng nhập lại.
  useEffect(() => dangKyXuLyMatPhien(() => setDaDangNhap(false)), [])

  // Đẩy hàng đợi mỗi khi có mạng trở lại, và định kỳ 30 giây.
  useEffect(() => startAutoSync(), [])

  // Access token chỉ sống 15 phút — làm mới định kỳ để nhân viên không bị đá
  // ra đăng nhập lại giữa ca.
  useEffect(() => {
    if (!daDangNhap) return
    const lam = () => { void lamMoiToken() }
    const t = window.setInterval(lam, 10 * 60_000)
    return () => window.clearInterval(t)
  }, [daDangNhap])

  // Nạp cache "đã đăng ký mặt chưa" khi có mạng, để màn chấm công dùng được
  // lúc mất sóng ở công trình. Thất bại thì thôi — cache cũ vẫn còn.
  useEffect(() => {
    if (!daDangNhap) return
    void layTrangThaiFace().catch(() => undefined)
  }, [daDangNhap])

  if (!daDangNhap) {
    return (
      <KhoThongBao>
        <DangNhap
          onXong={(phaiDoi) => { setPhaiDoiMatKhau(phaiDoi); setDaDangNhap(true) }}
        />
      </KhoThongBao>
    )
  }

  // Đặt TRƯỚC toàn bộ khung tab: không thanh tab, không đường vòng. Server đã
  // chặn mọi endpoint rồi — nếu giao diện vẫn cho vào các tab thì người dùng
  // chỉ gặp một chuỗi lỗi 403 không hiểu vì sao.
  if (phaiDoiMatKhau) {
    return (
      <KhoThongBao>
        <DoiMatKhau batBuoc onXong={() => setPhaiDoiMatKhau(false)} />
      </KhoThongBao>
    )
  }

  return (
    <KhoThongBao>
      <div className="ung-dung">
        <main className="noi-dung">
          <NoiDung />
        </main>
        <ThanhTab />
      </div>
    </KhoThongBao>
  )
}

/** `key` theo đường dẫn: đổi tab là ranh giới lỗi tự đặt lại, người dùng không
 *  bị kẹt ở màn báo lỗi của tab trước. */
function NoiDung() {
  const vt = useLocation()
  return (
    <RanhGioiLoi key={vt.pathname} vung={vt.pathname}>
      <Routes>
        <Route path="/" element={<Navigate to="/cham-cong" replace />} />
        <Route path="/cham-cong" element={<ChamCong />} />
        <Route path="/lich-su" element={<LichSu />} />
        <Route path="/yeu-cau" element={<YeuCau />} />
        <Route path="/ca-nhan" element={<CaNhan />} />
        <Route path="/ca-nhan/thong-tin" element={<ThongTinCaNhan />} />
        <Route path="/ca-cong-trinh" element={<CaCongTrinh />} />
        <Route path="*" element={<Navigate to="/cham-cong" replace />} />
      </Routes>
    </RanhGioiLoi>
  )
}

function ThanhTab() {
  const nv = nhanVienHienTai()

  return (
    <nav className="thanh-tab" aria-label="Điều hướng chính">
      {TABS.map((t) => (
        <NavLink
          key={t.den}
          to={t.den}
          className={({ isActive }) => `tab ${isActive ? 'dang-chon' : ''}`}
        >
          <span className="tab-bieu-tuong">
            {/* Tab "Cá nhân" dùng chữ cái đầu của chính người đang đăng nhập,
                theo bản thiết kế — nó vừa là biểu tượng vừa là lời nhắc đang
                đăng nhập bằng tài khoản nào, thứ đáng giá trên máy dùng chung. */}
            {t.den === '/ca-nhan' && nv
              ? <span className="tab-chu-cai">{chuCaiDau(nv.full_name)}</span>
              : <Icon ten={t.icon} size={22} />}
          </span>
          <span className="tab-nhan">{t.nhan}</span>
        </NavLink>
      ))}
    </nav>
  )
}
